import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { GoldSearchCreatorResult, GoldSearchMeta, MagicSearchService } from '../creators/service/magic-search/magic-search.service';
import { DecimalPipe } from '@angular/common';
import { NgxOrbComponent } from '@omnedia/ngx-orb';
import { NgxTypewriterComponent } from '@omnedia/ngx-typewriter';
import { GoldSearchItemComponent } from './molecules/gold-search-item/gold-search-item.component';
import { CreatorService } from '../../services/creator/creator.service';
import { CollaboratorService } from '../../services/collaborator/collaborator.service';
import { CollaboratorsComponent } from '../creators/molecules/collaborators/collaborators.component';
import { CreatorProfileComponent } from '../creators/molecules/creator-profile/creator-profile.component';
import { CreatorsForListComponent } from '../screening-briefs/molecules/creators-for-list/creators-for-list.component';
import { SingleVideoViewerSummaryComponent } from '../../atoms/single-video-viewer-summary/single-video-viewer-summary.component';
import { SocialScreeningService } from '../../services/social-screening/social-screening.service';

const SEARCH_LEAVE_MS = 500;
const HEADER_ENTER_MS = 350;
const HEADER_ENTER_DELAY_MS = 180;
const PAGE_SIZE = 20;

@Component({
  selector: 'app-creators-v4',
  imports: [DecimalPipe, NgxOrbComponent,
    NgxTypewriterComponent, GoldSearchItemComponent, CollaboratorsComponent, 
    CreatorProfileComponent, CreatorsForListComponent, 
    SingleVideoViewerSummaryComponent],
  templateUrl: './creators-v4.component.html',
  styleUrl: './creators-v4.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('searchBlockLeave', [
      state('visible', style({ transform: 'translateY(0)', width: '100%', opacity: 1 })),
      state('leaving', style({
        transform: 'translateY(-65vh)',
        width: '120%',
        opacity: 0,
      })),
      transition('visible => leaving', [
        animate(`${SEARCH_LEAVE_MS}ms ease-in-out`),
      ]),
    ]),
    trigger('headerEnter', [
      transition(':enter', [
        style({
          opacity: 0,
          transform: 'translateY(12px)',
        }),
        animate(
          `${HEADER_ENTER_MS}ms ${HEADER_ENTER_DELAY_MS}ms ease-out`,
          style({ opacity: 1, transform: 'translateY(0)' })
        ),
      ]),
    ]),
  ],
})
export class CreatorsV4Component implements OnInit {

  searchExamples: string[] = [
    'Fitness creators in London, 50k+ followers',
    'Female beauty influencers, TikTok native',
    'Tech reviewers with high engagement',
    'Lifestyle creators, mums, UK-based',
  ];

  initialState = signal<boolean>(true);
  isTransitioning = signal<boolean>(false);
  showFilters = signal<boolean>(false);

  inputValue = signal('');
  isLoading = signal<boolean>(false);
  isPaginating = signal<boolean>(false);

  allResults = signal<GoldSearchCreatorResult[]>([]);
  searchMeta = signal<GoldSearchMeta | undefined>(undefined);
  currentSkip = signal(0);
  errorMessage = signal('');

  get hasMore(): boolean {
    const meta = this.searchMeta();
    return !!meta && this.allResults().length < meta.total;
  }

  /** Services */
  protected magicSearchService = inject(MagicSearchService);
  private creatorService = inject(CreatorService);
  public collaboratorsService = inject(CollaboratorService);
  public socialScreeningService = inject(SocialScreeningService);
  /** Services - END */

  /** Disable search when input is not a valid string with more than 3 characters */
  searchDisabled = computed(() => {
    const v = this.inputValue();
    return typeof v !== 'string' || v.trim().length <= 3;
  });


  ngOnInit(): void {
    this.initialState.set(true);
    this.creatorService.getShoutCreatorInterests().subscribe({
      next: (interests: any) => {
        this.creatorService.shoutCreatorInterests = interests;
      },
      error: (err: any) => {
        console.warn('Error getting interests: ', err);
      }
    });
  }

  quickSearch(example: string): void {
    this.inputValue.set(example);
    this.search();
  }

  onSearchKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (!this.searchDisabled() && !this.isLoading()) {
        this.search();
      }
    }
  }

  search(): void {
    console.log('💛💛💛 Search:', this.inputValue());
    console.log('💛💛💛 isTransitioning:', this.isTransitioning());
    if (this.isTransitioning()) return;
    this.isTransitioning.set(true);
    this.isLoading.set(true);

    this.allResults.set([]);
    this.searchMeta.set(undefined);
    this.currentSkip.set(0);
    this.errorMessage.set('');
    this.magicSearchService.kickOffGoldSearch(this.inputValue(), 0, PAGE_SIZE).subscribe({
      next: (response) => {
        console.log('💛💛💛 Gold Search Results:', response);
        this.allResults.set(response.results);
        this.searchMeta.set(response.meta);
        this.currentSkip.set(response.results.length);
        this.isLoading.set(false);
        this.isTransitioning.set(false);
      },
      error: () => {
        this.errorMessage.set('Something went wrong. Please try again.');
        this.isLoading.set(false);
        this.isTransitioning.set(false);
      }
    });
  }

  loadMore(): void {
    if (this.isPaginating() || !this.hasMore) return;
    this.isPaginating.set(true);
    this.magicSearchService.kickOffGoldSearch(this.inputValue(), this.currentSkip(), PAGE_SIZE).subscribe({
      next: (response) => {
        console.log('💛💛💛 Gold Search Next Page:', response);
        this.allResults.update(existing => [...existing, ...response.results]);
        this.searchMeta.set(response.meta);
        this.currentSkip.update(s => s + response.results.length);
        this.isPaginating.set(false);
      },
      error: () => {
        this.isPaginating.set(false);
      }
    });
  }

  onSearchBlockLeaveDone(): void {
    if (this.isTransitioning()) {
      this.initialState.set(false);
      this.isTransitioning.set(false);
    }
  }

  onSendMessage(message: string): void {
    console.log('AI assistant message:', message);
  }


  toggleFilters(): void {
    this.showFilters.set(!this.showFilters());
  }

}
